import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Heart, 
  MessageCircle, 
  ArrowLeft, 
  Share2, 
  Clock, 
  MapPin,
  User,
  Calendar,
  ExternalLink
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { monumentAPI } from '../services/api';
import { getComments, createComment, likePost, unlikePost } from '../services/supabase';
import toast from 'react-hot-toast';
import LoadingSkeleton from '../components/UI/LoadingSkeleton';

const StoryPage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const source = searchParams.get('source') || 'wikipedia';
  const { isAuthenticated, requireAuth } = useAuth();
  const [monument, setMonument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [showComments, setShowComments] = useState(false);
  const [relatedMonuments, setRelatedMonuments] = useState([]);
  
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ container: containerRef });

  // Safe coordinates helper
  const safeCoordinates = useMemo(() => {
    if (!monument?.coordinates) return null;
    
    if (Array.isArray(monument.coordinates)) {
      const [lng, lat] = monument.coordinates;
      if (typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
    } else if (monument.coordinates.lat && monument.coordinates.lng) {
      const lat = parseFloat(monument.coordinates.lat);
      const lng = parseFloat(monument.coordinates.lng);
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
    }
    
    return null;
  }, [monument?.coordinates]);
  
  // Smooth spring animation for scroll progress
  const smoothProgress = useSpring(scrollYProgress, { stiffness: 400, damping: 40 });
  
  // Transform values for parallax effects
  const backgroundY = useTransform(smoothProgress, [0, 1], [0, -100]);
  const textY = useTransform(smoothProgress, [0, 1], [0, 50]);
  const opacity = useTransform(smoothProgress, [0, 0.2, 0.8, 1], [1, 0.8, 0.8, 0]);

  useEffect(() => {
    fetchMonument();
  }, [slug]);

  useEffect(() => {
    if (showComments && monument) {
      fetchComments();
    }
  }, [showComments, monument]);

  const fetchMonument = async () => {
    try {
      const monumentData = await monumentAPI.getMonumentDetails(slug, source);
      if (monumentData) {
        setMonument(monumentData);
        
        // Fetch related monuments
        if (monumentData.title) {
          const related = await monumentAPI.searchMonuments(monumentData.title.split(' ')[0], { limit: 4 });
          setRelatedMonuments(related.filter(m => m.id !== monumentData.id || m.source !== monumentData.source));
        }
      } else {
        throw new Error('Monument not found');
      }
    } catch (error) {
      console.error('Failed to fetch monument:', error);
      toast.error('Failed to load story');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      // For static hosting, we'll use monument ID as post ID
      const postId = `${monument.source}-${monument.id}`;
      const { data: commentsData } = await getComments(postId);
      setComments(commentsData || []);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      // Comments are optional, so don't show error to user
    }
  };

  const handleLike = async () => {
    if (!requireAuth(() => {})) return;

    try {
      // For static hosting, likes are simulated locally
      const postId = `${monument.source}-${monument.id}`;
      const isLiked = monument.isLiked || false;
      
      if (isLiked) {
        await unlikePost(postId, 'user-id-placeholder');
        setMonument(prev => ({
          ...prev,
          likes: Math.max(0, (prev.likes || 0) - 1),
          isLiked: false
        }));
        toast.success('Like removed');
      } else {
        await likePost(postId, 'user-id-placeholder');
        setMonument(prev => ({
          ...prev,
          likes: (prev.likes || 0) + 1,
          isLiked: true
        }));
        toast.success('Story liked!');
      }
    } catch (error) {
      console.error('Failed to update like:', error);
      toast.error('Failed to update like');
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!requireAuth(() => {})) return;

    if (!newComment.trim()) return;

    try {
      const postId = `${monument.source}-${monument.id}`;
      const { data: commentData } = await createComment({
        post_id: postId,
        text: newComment.trim(),
        parent_comment_id: null
      });
      
      if (commentData) {
        setComments(prev => [commentData, ...prev]);
        setNewComment('');
        toast.success('Comment added successfully');
      }
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: monument.title || 'Historical Monument',
      text: `Discover the fascinating history of ${monument.title || 'this monument'} on Historer`,
      url: window.location.href
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.error('Failed to share:', error);
        // Fallback to clipboard
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      toast.success('Link copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  const ChapterSection = ({ chapter, index }) => {
    const [ref, inView] = useInView({
      threshold: 0.5,
      root: containerRef.current
    });

    useEffect(() => {
      if (inView) {
        setCurrentChapterIndex(index);
      }
    }, [inView, index]);

    return (
      <motion.section
        ref={ref}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        data-aos="fade-up"
      >
        {/* Background Image */}
        <motion.div
          className="absolute inset-0 z-0"
          style={{ y: backgroundY }}
        >
          {chapter.image ? (
            <img
              src={chapter.image}
              alt={chapter.header}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary-100 via-primary-50 to-primary-200"></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-primary-50/80 via-primary-50/90 to-primary-50"></div>
        </motion.div>

        {/* Content */}
        <motion.div
          className="relative z-10 max-w-4xl mx-auto px-6 text-center"
          style={{ y: textY, opacity }}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: index * 0.1 }}
        >
          <motion.h2
            className="text-4xl md:text-6xl font-serif font-bold text-text-primary mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: index * 0.1 + 0.2 }}
          >
            {chapter.header}
          </motion.h2>
          
          <motion.div
            className="glass-card max-w-3xl mx-auto text-left"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: index * 0.1 + 0.4 }}
          >
            <div className="prose prose-lg prose-invert max-w-none">
              {chapter.body.split('\n\n').map((paragraph, pIndex) => (
                <p key={pIndex} className="text-text-primary leading-relaxed mb-6">
                  {paragraph}
                </p>
              ))}
            </div>
          </motion.div>
        </motion.div>

        {/* Chapter Indicator */}
        {monument.chapters && Array.isArray(monument.chapters) && (
          <div className="absolute left-8 top-1/2 transform -translate-y-1/2 z-20">
            <div className="flex flex-col items-center space-y-2">
              {monument.chapters.map((_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    const element = document.getElementById(`chapter-${i}`);
                    element?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    i === currentChapterIndex
                      ? 'bg-accent-primary w-8'
                      : 'bg-text-muted hover:bg-text-secondary'
                  }`}
                />
              ))}
            </div>
          </div>
        )}
      </motion.section>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!monument) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-serif font-bold text-text-primary mb-4">
            Story Not Found
          </h2>
          <p className="text-text-secondary mb-6">
            The historical story you're looking for doesn't exist.
          </p>
          <Link to="/discover" className="btn-primary">
            Discover Other Stories
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-primary-50">
      {/* Progress Bar */}
      <motion.div
        className="fixed top-16 left-0 right-0 h-1 bg-accent-primary z-50 origin-left"
        style={{ scaleX: smoothProgress }}
      />

      {/* Navigation Header */}
      <motion.div
        className="fixed top-20 left-0 right-0 z-40 glass-morphism border-b border-glass-border"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/discover"
                className="flex items-center space-x-2 text-text-secondary hover:text-text-primary transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Discover</span>
              </Link>
              <div className="h-6 w-px bg-glass-border"></div>
              <h1 className="text-xl font-serif font-semibold text-text-primary">
                {monument.title}
              </h1>
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={handleLike}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-300 ${
                  monument.isLiked
                    ? 'bg-accent-primary/20 text-accent-primary'
                    : 'glass-morphism text-text-secondary hover:text-text-primary'
                }`}
              >
                <Heart className={`w-5 h-5 ${monument.isLiked ? 'fill-current' : ''}`} />
                <span>{monument.likes || 0}</span>
              </button>

              <button
                onClick={() => setShowComments(!showComments)}
                className="flex items-center space-x-2 px-4 py-2 glass-morphism text-text-secondary hover:text-text-primary rounded-lg transition-all duration-300"
              >
                <MessageCircle className="w-5 h-5" />
                <span>{comments.length}</span>
              </button>

              <button
                onClick={handleShare}
                className="p-2 glass-morphism text-text-secondary hover:text-text-primary rounded-lg transition-all duration-300"
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Story Content */}
      <div
        ref={containerRef}
        className="overflow-y-auto"
        style={{ height: '100vh', scrollBehavior: 'smooth' }}
      >
        {/* Hero Section */}
        <motion.section
          className="relative min-h-screen flex items-center justify-center overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <div className="absolute inset-0">
            {monument.mainImage ? (
              <img
                src={monument.mainImage}
                alt={monument.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-primary-100 via-primary-50 to-primary-200"></div>
            )}
            <div className="absolute inset-0 bg-gradient-to-b from-primary-50/30 via-primary-50/60 to-primary-50"></div>
          </div>

          <motion.div
            className="relative z-10 text-center px-6 max-w-4xl mx-auto"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="glass-card p-8">
              <h1 className="text-5xl md:text-7xl font-serif font-bold text-text-primary mb-6">
                {monument.title}
              </h1>
              
              <div className="flex items-center justify-center space-x-6 text-text-secondary mb-8">
                <div className="flex items-center space-x-2">
                  <Clock className="w-5 h-5" />
                  <span>{monument.era}</span>
                </div>
                {safeCoordinates && (
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-5 h-5" />
                    <span>
                      {safeCoordinates.lat.toFixed(4)}°, {safeCoordinates.lng.toFixed(4)}°
                    </span>
                  </div>
                )}
              </div>

              <p className="text-xl text-text-secondary leading-relaxed">
                Journey through time and discover the fascinating history of this remarkable monument.
              </p>
            </div>
          </motion.div>

          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-accent-primary rounded-full flex justify-center">
              <div className="w-1 h-3 bg-accent-primary rounded-full mt-2 animate-pulse"></div>
            </div>
          </div>
        </motion.section>

        {/* Chapters */}
        {monument.chapters && Array.isArray(monument.chapters) && monument.chapters.map((chapter, index) => (
          <div key={index} id={`chapter-${index}`}>
            <ChapterSection chapter={chapter} index={index} />
          </div>
        ))}

        {/* End Section */}
        <motion.section
          className="relative min-h-screen flex items-center justify-center overflow-hidden"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary-100 via-primary-50 to-primary-200"></div>
          
          <motion.div
            className="relative z-10 text-center px-6 max-w-4xl mx-auto"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="glass-card p-8">
              <h2 className="text-4xl font-serif font-bold text-text-primary mb-6">
                Continue Your Journey
              </h2>
              <p className="text-xl text-text-secondary mb-8">
                Explore more historical stories and discover the rich tapestry of our past.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/discover" className="btn-primary">
                  Discover More Stories
                </Link>
                <button
                  onClick={() => setShowComments(true)}
                  className="btn-secondary"
                >
                  Join the Discussion
                </button>
              </div>
            </div>
          </motion.div>
        </motion.section>
      </div>

      {/* Comments Sidebar */}
      {showComments && (
        <motion.div
          className="fixed right-0 top-20 bottom-0 w-96 glass-morphism border-l border-glass-border z-30 overflow-hidden"
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ duration: 0.3 }}
        >
          <div className="h-full flex flex-col">
            {/* Comments Header */}
            <div className="p-4 border-b border-glass-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-serif font-semibold text-text-primary">
                  Discussion ({comments.length})
                </h3>
                <button
                  onClick={() => setShowComments(false)}
                  className="p-2 text-text-secondary hover:text-text-primary rounded-lg transition-colors"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Comment Form */}
            {isAuthenticated && (
              <form onSubmit={handleComment} className="p-4 border-b border-glass-border">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Share your thoughts..."
                  className="w-full p-3 glass-morphism rounded-lg text-text-primary placeholder-text-muted resize-none focus:outline-none focus:ring-2 focus:ring-accent-primary/50"
                  rows={3}
                />
                <button
                  type="submit"
                  disabled={!newComment.trim()}
                  className="mt-2 btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Post Comment
                </button>
              </form>
            )}

            {/* Comments List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {comments.length === 0 ? (
                <div className="text-center py-8">
                  <MessageCircle className="w-12 h-12 text-text-muted mx-auto mb-4" />
                  <p className="text-text-secondary">No comments yet. Be the first to share your thoughts!</p>
                </div>
              ) : (
                comments.map((comment) => (
                  <div key={comment._id} className="glass-card p-4">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-8 h-8 bg-accent-primary/20 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-accent-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-text-primary">{comment.author?.username || 'Anonymous'}</p>
                        <p className="text-xs text-text-muted">
                          {comment.created_at ? new Date(comment.created_at).toLocaleDateString() : 'Just now'}
                        </p>
                      </div>
                    </div>
                    <p className="text-text-primary">{comment.text}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default StoryPage;
